/**
 * Check if a pharmacy is currently open based on opening hours
 * @param {Array} opening_hours - Array of opening hours data
 * @returns {boolean} - true if pharmacy is open, false if closed
 */
export const isPharmacyOpen = (opening_hours) => {
  if (!opening_hours || opening_hours.length === 0) {
    return false;
  }

  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();

  const dayIndex = currentDay === 0 ? 7 : currentDay;

  const todayHours = opening_hours.find(entry => entry.day === dayIndex);

  if (!todayHours || todayHours.isOpen !== 1) {
    return false;
  }

  try {
    let starts, ends;
    
    if (typeof todayHours.start === 'string' && todayHours.start.startsWith('[')) {
      starts = JSON.parse(todayHours.start);
      ends = JSON.parse(todayHours.end);
    } else {
      starts = Array.isArray(todayHours.start) ? todayHours.start : [todayHours.start];
      ends = Array.isArray(todayHours.end) ? todayHours.end : [todayHours.end];
    }

    for (let i = 0; i < starts.length; i++) {
      const startTime = parseTimeToMinutes(starts[i]);
      const endTime = parseTimeToMinutes(ends[i]);

      if (currentTime >= startTime && currentTime <= endTime) {
        return true;
      }
    }
  } catch (e) {
    const startTime = parseTimeToMinutes(todayHours.start);
    const endTime = parseTimeToMinutes(todayHours.end);

    if (currentTime >= startTime && currentTime <= endTime) {
      return true;
    }
  }

  return false;
};

/**
 * Get the closing time for today if pharmacy is open
 * @param {Array} opening_hours - Array of opening hours data
 * @returns {string|null} - closing time in HH:MM format or null if closed
 */
export const getClosingTime = (opening_hours) => {
  if (!opening_hours || opening_hours.length === 0) {
    return null;
  }

  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();

  const dayIndex = currentDay === 0 ? 7 : currentDay;

  const todayHours = opening_hours.find(entry => entry.day === dayIndex);

  if (!todayHours || todayHours.isOpen !== 1) {
    return null;
  }

  try {
    let starts, ends;
    
    if (typeof todayHours.start === 'string' && todayHours.start.startsWith('[')) {
      starts = JSON.parse(todayHours.start);
      ends = JSON.parse(todayHours.end);
    } else {
      starts = Array.isArray(todayHours.start) ? todayHours.start : [todayHours.start];
      ends = Array.isArray(todayHours.end) ? todayHours.end : [todayHours.end];
    }

    for (let i = 0; i < starts.length; i++) {
      const startTime = parseTimeToMinutes(starts[i]);
      const endTime = parseTimeToMinutes(ends[i]);

      if (currentTime >= startTime && currentTime <= endTime) {
        return ends[i];
      }
    }
  } catch (e) {
    const startTime = parseTimeToMinutes(todayHours.start);
    const endTime = parseTimeToMinutes(todayHours.end);

    if (currentTime >= startTime && currentTime <= endTime) {
      return todayHours.end;
    }
  }

  return null;
};

/**
 * Get the next opening time for a closed pharmacy
 * @param {Array} opening_hours - Array of opening hours data
 * @returns {string|null} - next opening time or null if no opening hours
 */
/**
 * Get today's closing time for a closed pharmacy
 * @param {Array} opening_hours - Array of opening hours data
 * @returns {string|null} - closing time for today or null if no hours today
 */
export const getTodayClosingTime = (opening_hours) => {
  if (!opening_hours || opening_hours.length === 0) {
    return null;
  }

  const now = new Date();
  const currentDay = now.getDay();
  const dayIndex = currentDay === 0 ? 7 : currentDay;

  const todayHours = opening_hours.find(entry => entry.day === dayIndex);

  if (!todayHours || todayHours.isOpen !== 1) {
    return null;
  }

  try {
    let ends;
    if (typeof todayHours.end === 'string' && todayHours.end.startsWith('[')) {
      ends = JSON.parse(todayHours.end);
    } else {
      ends = Array.isArray(todayHours.end) ? todayHours.end : [todayHours.end];
    }
    return ends[ends.length - 1]; // Return last closing time of the day
  } catch (e) {
    return todayHours.end;
  }
};

export const getNextOpeningTime = (opening_hours) => {
  if (!opening_hours || opening_hours.length === 0) {
    return null;
  }

  const now = new Date();
  const currentDay = now.getDay();
  const dayIndex = currentDay === 0 ? 7 : currentDay;

  // Check today first
  const todayHours = opening_hours.find(entry => entry.day === dayIndex);
  if (todayHours && todayHours.isOpen === 1) {
    try {
      let starts;
      if (typeof todayHours.start === 'string' && todayHours.start.startsWith('[')) {
        starts = JSON.parse(todayHours.start);
      } else {
        starts = Array.isArray(todayHours.start) ? todayHours.start : [todayHours.start];
      }
      return starts[0]; // Return first opening time of the day
    } catch (e) {
      return todayHours.start;
    }
  }

  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = ((dayIndex + i - 1) % 7) + 1;
    const nextDay = nextDayIndex === 8 ? 1 : nextDayIndex;
    
    const dayHours = opening_hours.find(entry => entry.day === nextDay);
    if (dayHours && dayHours.isOpen === 1) {
      try {
        let starts;
        if (typeof dayHours.start === 'string' && dayHours.start.startsWith('[')) {
          starts = JSON.parse(dayHours.start);
        } else {
          starts = Array.isArray(dayHours.start) ? dayHours.start : [dayHours.start];
        }
        return starts[0];
      } catch (e) {
        return dayHours.start;
      }
    }
  }

  return null;
};

/**
 * Get the next opening day and time for a closed pharmacy
 * @param {Array} opening_hours - Array of opening hours data
 * @returns {Object|null} - {dayName: string, time: string, date: Date} or null if no opening hours
 */
export const getNextOpeningDayAndTime = (opening_hours, t) => {
  if (!opening_hours || opening_hours.length === 0) {
    return null;
  }

  const now = new Date();
  const currentDay = now.getDay();
  const dayIndex = currentDay === 0 ? 7 : currentDay;
  
  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Check next 7 days starting from tomorrow
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = ((dayIndex + i - 1) % 7) + 1;
    const nextDay = nextDayIndex === 8 ? 1 : nextDayIndex;
    
    const dayHours = opening_hours.find(entry => entry.day === nextDay);
    if (dayHours && dayHours.isOpen === 1) {
      const checkDate = new Date();
      checkDate.setDate(checkDate.getDate() + i);
      
      try {
        let starts;
        if (typeof dayHours.start === 'string' && dayHours.start.startsWith('[')) {
          starts = JSON.parse(dayHours.start);
        } else {
          starts = Array.isArray(dayHours.start) ? dayHours.start : [dayHours.start];
        }
        
        const dayNameIndex = nextDay === 7 ? 0 : nextDay;
        const dayName = t ? t(`common.days.${dayKeys[dayNameIndex]}`) : dayKeys[dayNameIndex];
        return {
          dayName: dayName,
          time: starts[0],
          date: checkDate
        };
      } catch (e) {
        const dayNameIndex = nextDay === 7 ? 0 : nextDay;
        const dayName = t ? t(`common.days.${dayKeys[dayNameIndex]}`) : dayKeys[dayNameIndex];
        return {
          dayName: dayName,
          time: dayHours.start,
          date: checkDate
        };
      }
    }
  }

  return null;
};

/**
 * Get upcoming night duty in the next 7 days
 * @param {string} nextNightDuty - Next night duty date from pharmacy data (DD.MM.YYYY format)
 * @param {number} interval - Night duty interval in days
 * @returns {Object|null} - {dayName: string, date: Date} or null if no night duty
 */
const getUpcomingNightDuty = (nextNightDuty, interval, t) => {
  if (!nextNightDuty || !interval) {
    return null;
  }

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    // Check next 7 days starting from tomorrow
    for (let i = 1; i <= 7; i++) {
      const checkDate = new Date();
      checkDate.setDate(checkDate.getDate() + i);
      
      if (isPharmacyOnNightDuty(nextNightDuty, interval, checkDate)) {
        const dayName = t ? t(`common.days.${dayKeys[checkDate.getDay()]}`) : dayKeys[checkDate.getDay()];
        return {
          dayName: dayName,
          date: checkDate
        };
      }
    }
    
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Check if pharmacy is on night duty for a specific date
 * @param {string} nextNightDuty - Next night duty date from pharmacy data (DD.MM.YYYY format)
 * @param {number} interval - Night duty interval in days
 * @param {Date} checkDate - Date to check (defaults to today)
 * @returns {boolean} - true if pharmacy is on night duty on the specified date
 */
export const isPharmacyOnNightDuty = (nextNightDuty, interval, checkDate = new Date()) => {
  if (!nextNightDuty || !interval) {
    return false;
  }

  try {
    const targetDate = new Date(checkDate);
    targetDate.setHours(0, 0, 0, 0);
    
    // Parse DD.MM.YYYY format
    const [day, month, year] = nextNightDuty.split('.');
    const nightDutyDate = new Date(year, month - 1, day);
    
    // Calculate days difference
    const timeDiff = targetDate.getTime() - nightDutyDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    
    // Check if the target date matches the night duty cycle
    return daysDiff >= 0 && daysDiff % interval === 0;
  } catch (e) {
    return false;
  }
};

/**
 * Check if pharmacy is on night duty tomorrow
 * @param {string} nextNightDuty - Next night duty date from pharmacy data (DD.MM.YYYY format)
 * @param {number} interval - Night duty interval in days
 * @returns {boolean} - true if pharmacy is on night duty tomorrow
 */
export const isPharmacyOnNightDutyTomorrow = (nextNightDuty, interval) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return isPharmacyOnNightDuty(nextNightDuty, interval, tomorrow);
};

/**
 * Get comprehensive pharmacy status including night duty
 * @param {Array} opening_hours - Array of opening hours data
 * @param {string} nextNightDuty - Next night duty date from pharmacy data (DD.MM.YYYY format)
 * @param {number} interval - Night duty interval in days
 * @param {Function} t - Translation function
 * @returns {Object} - {isOpen: boolean, statusText: string, statusTimeText: string}
 */
export const getPharmacyStatus = (opening_hours, nextNightDuty, interval, t) => {
  const isRegularOpen = isPharmacyOpen(opening_hours);
  const closingTime = getClosingTime(opening_hours);
  const onNightDutyToday = isPharmacyOnNightDuty(nextNightDuty, interval);
  const onNightDutyTomorrow = isPharmacyOnNightDutyTomorrow(nextNightDuty, interval);
  
  // Priority 1: Currently open during regular hours
  if (isRegularOpen) {
    return {
      isOpen: true,
      statusText: t('component.search.pharmacy.status.open'),
      statusTimeText: closingTime ? t('component.search.pharmacy.status.closes_at', { time: closingTime }) : null
    };
  }
  
  // Priority 2: Night duty today (check if it's after 20:00 or before 08:00)
  if (onNightDutyToday) {
    const now = new Date();
    const currentHour = now.getHours();
    
    // Night duty is active from 20:00 to 08:00 next day
    if (currentHour >= 20 || currentHour < 8) {
      return {
        isOpen: true,
        statusText: t('component.search.pharmacy.status.open'),
        statusTimeText: t('component.search.pharmacy.status.night_duty_until', { time: '08:00' })
      };
    } else {
      // It's night duty day but before 20:00
      return {
        isOpen: false,
        statusText: t('component.search.pharmacy.status.closed'),
        statusTimeText: t('component.search.pharmacy.status.open_today_at', { time: '20:00' })
      };
    }
  }
  
  // Priority 3: Night duty tomorrow
  if (onNightDutyTomorrow) {
    return {
      isOpen: false,
      statusText: t('component.search.pharmacy.status.closed'),
      statusTimeText: t('component.search.pharmacy.status.open_tomorrow_at', { time: '20:00' })
    };
  }
  
  // Priority 4: Check for upcoming night duty in next 7 days
  const upcomingNightDuty = getUpcomingNightDuty(nextNightDuty, interval, t);
  const nextOpeningInfo = getNextOpeningDayAndTime(opening_hours, t);
  
  // If both exist, show whichever comes first
  if (upcomingNightDuty && nextOpeningInfo) {
    if (upcomingNightDuty.date <= nextOpeningInfo.date) {
      return {
        isOpen: false,
        statusText: t('component.search.pharmacy.status.closed'),
        statusTimeText: t('component.search.pharmacy.status.open_on_day_at', { day: upcomingNightDuty.dayName, time: '20:00' })
      };
    } else {
      return {
        isOpen: false,
        statusText: t('component.search.pharmacy.status.closed'),
        statusTimeText: t('component.search.pharmacy.status.open_on_day_at', { day: nextOpeningInfo.dayName, time: nextOpeningInfo.time })
      };
    }
  }
  
  // Priority 5: Next regular opening time (if no night duty)
  if (nextOpeningInfo) {
    return {
      isOpen: false,
      statusText: t('component.search.pharmacy.status.closed'),
      statusTimeText: t('component.search.pharmacy.status.open_on_day_at', { day: nextOpeningInfo.dayName, time: nextOpeningInfo.time })
    };
  }
  
  // Priority 6: Next night duty (if no regular opening)
  if (upcomingNightDuty) {
    return {
      isOpen: false,
      statusText: t('component.search.pharmacy.status.closed'),
      statusTimeText: t('component.search.pharmacy.status.open_on_day_at', { day: upcomingNightDuty.dayName, time: '20:00' })
    };
  }
  
  return {
    isOpen: false,
    statusText: t('component.search.pharmacy.status.closed'),
    statusTimeText: null
  };
};

/**
 * Convert time string (HH:MM) to minutes
 * @param {string} timeStr - Time in HH:MM format
 * @returns {number} - Time in minutes
 */
const parseTimeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};